/**
 * Participant API client surface (docs/VOCABULARY.md "Route surface" → method names).
 *
 * TODO(api-client): replace this interface with the exported client of `@windrop/api-client` once
 * `packages/api-client/src` ships one (docs/modules/api-client.md). Method names here follow the VOCABULARY route
 * list so the swap is mechanical; `src/lib/api/index.ts` is the only module that constructs a client.
 */
import type {
  CheckoutDto,
  ClaimAppealRequest,
  ClaimCatalogDto,
  FinalizeEvidenceResponse,
  PresignEvidenceRequest,
  PresignEvidenceResponse,
  SaveClaimDraftRequest,
  ClaimSessionResponse,
  ClaimStatusDto,
  ClientEventBatchRequest,
  CompleteTaskRequest,
  CreateEntryRequest,
  CreateEntryResponse,
  EntryStatusDto,
  QuickPickRequest,
  QuickPickResponse,
  CreateHoldRequest,
  CreateSupportCaseRequest,
  CurrentClaimDto,
  GoRedirectResponseDto,
  SocialPlatform,
  WinSummaryDto,
  EntryReceiptDto,
  FairnessCommitmentDto,
  HoldDto,
  InitializePaymentRequest,
  LinkChannelRequest,
  ChannelDto,
  PaymentStatusDto,
  ProblemDetails,
  PublicLotteryDto,
  ReferralResolutionDto,
  ResultsDto,
  RevealResultDto,
  SubmitClaimRequest,
  SupportCaseDto,
  TaskCompletionDto,
  WinLinkDto,
} from '@windrop/contracts';

export interface RequestOptions {
  signal?: AbortSignal;
  /** Mutations: `Idempotency-Key` header (§15.1). Callers keep the same key across retries (§4.4). */
  idempotencyKey?: string;
}

/** RFC 9457 problem returned by the API, as a thrown error. */
export class ApiError extends Error {
  readonly problem: ProblemDetails;
  readonly status: number;
  /** `https://windrop.dev/problems/<code>` → `<code>`. */
  readonly code: string;
  constructor(problem: ProblemDetails) {
    super(problem.detail ?? problem.title);
    this.name = 'ApiError';
    this.problem = problem;
    this.status = problem.status;
    this.code = problem.type.split('/').pop() ?? 'unknown';
  }
}

/** Network failure (offline, DNS, aborted) — distinct from an API problem so the UI can say "check status". */
export class NetworkError extends Error {
  constructor(message = 'network_unavailable', cause?: unknown) {
    super(message, cause === undefined ? undefined : { cause });
    this.name = 'NetworkError';
  }
}

export interface WebVitalsBeacon {
  name: 'LCP' | 'INP' | 'CLS' | 'TTFB';
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  route: string;
  navigationType?: string;
  buildSha: string;
}

export interface WindropClient {
  // lotteries
  getLottery(slug: string, opts?: RequestOptions): Promise<PublicLotteryDto>;
  // entries
  createEntry(
    slug: string,
    body: CreateEntryRequest,
    opts: RequestOptions & { idempotencyKey: string },
  ): Promise<CreateEntryResponse>;
  getEntryReceipt(entryId: string, opts?: RequestOptions): Promise<EntryReceiptDto>;
  /** `GET /v1/entries/:id/status` — the §4.4 "check status" read; never re-submits. */
  getEntryStatus(entryId: string, opts?: RequestOptions): Promise<EntryStatusDto>;
  /** `POST /v1/l/:slug/quick-pick` — server-generated picks (§4.3). */
  quickPick(slug: string, body: QuickPickRequest, opts?: RequestOptions): Promise<QuickPickResponse>;
  // payments
  initializePayment(
    body: InitializePaymentRequest,
    opts: RequestOptions & { idempotencyKey: string },
  ): Promise<CheckoutDto>;
  getPaymentStatus(paymentId: string, opts?: RequestOptions): Promise<PaymentStatusDto>;
  // engagement
  completeTask(
    taskId: string,
    body: CompleteTaskRequest,
    opts: RequestOptions & { idempotencyKey: string },
  ): Promise<TaskCompletionDto>;
  linkChannel(body: LinkChannelRequest, opts?: RequestOptions): Promise<ChannelDto>;
  resolveReferral(code: string, opts?: RequestOptions): Promise<ReferralResolutionDto>;
  // outcome
  /** `entryId` adds the participant lookup (`?entryId=`), outcome agent. */
  getResults(slug: string, opts?: RequestOptions & { entryId?: string }): Promise<ResultsDto>;
  getFairness(drawId: string, opts?: RequestOptions): Promise<FairnessCommitmentDto>;
  /** §4.2 D: the server-decided outcome of an entry, fetched before any reveal animation (outcome agent). */
  getReveal(entryId: string, opts?: RequestOptions & { slug?: string }): Promise<RevealResultDto>;
  // claims
  openClaimSession(token: string, opts?: RequestOptions): Promise<ClaimSessionResponse>;
  getCurrentClaim(opts?: RequestOptions): Promise<CurrentClaimDto>;
  getClaimCatalog(opts?: RequestOptions): Promise<ClaimCatalogDto>;
  holdVariant(body: CreateHoldRequest, opts: RequestOptions & { idempotencyKey: string }): Promise<HoldDto>;
  submitClaim(
    body: SubmitClaimRequest,
    opts: RequestOptions & { idempotencyKey: string },
  ): Promise<ClaimStatusDto>;
  getClaimStatus(opts?: RequestOptions): Promise<ClaimStatusDto>;
  // winners
  getWinLink(
    winId: string,
    query: { exp?: string; sig?: string },
    opts?: RequestOptions,
  ): Promise<WinLinkDto & Partial<WinSummaryDto>>;
  /** `GET /v1/go/:winId/:platform` as JSON (records the tap; returns the deep link + copyable message, §12.2). */
  getGoRedirect(
    winId: string,
    platform: SocialPlatform,
    opts?: RequestOptions,
  ): Promise<GoRedirectResponseDto>;
  // support
  createSupportCase(
    body: CreateSupportCaseRequest,
    opts: RequestOptions & { idempotencyKey: string },
  ): Promise<SupportCaseDto>;
  /** `GET /v1/support/cases/:ref` — participant view by case reference (no PII in the URL). */
  getSupportCase(caseRef: string, opts?: RequestOptions): Promise<SupportCaseDto>;
  // telemetry (fire-and-forget; implementations must never throw)
  postAnalyticsEvents(body: ClientEventBatchRequest, opts?: { beacon?: boolean }): Promise<void>;
  postVitals(body: WebVitalsBeacon, opts?: { beacon?: boolean }): Promise<void>;
  // claims (storefront + verify + status, appended by the claims agent)
  extendHold(opts: RequestOptions & { idempotencyKey: string }): Promise<HoldDto>;
  presignEvidence(body: PresignEvidenceRequest, opts?: RequestOptions): Promise<PresignEvidenceResponse>;
  finalizeEvidence(uploadId: string, opts?: RequestOptions): Promise<FinalizeEvidenceResponse>;
  saveClaimDraft(
    body: SaveClaimDraftRequest,
    opts?: RequestOptions,
  ): Promise<{ savedFields: Record<string, string> }>;
  appealClaim(
    body: ClaimAppealRequest,
    opts: RequestOptions & { idempotencyKey: string },
  ): Promise<ClaimStatusDto>;
}
